import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Videocam,
  CheckCircle,
  Cancel,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { liveCameraService } from '../../services/liveCameraService';

const LiveCameraManagement = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingLiveCamera, setEditingLiveCamera] = useState(null);
  const [formData, setFormData] = useState({
    stream_key: '',
    rtmp_url: '',
    stream_url: '',
    resolution: '1920x1080',
    bitrate: 2500,
    fps: 30,
    is_active: true
  });
  const [showStreamKey, setShowStreamKey] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const queryClient = useQueryClient();

  // Buscar informações da câmera ao vivo
  const { data: liveCameraData, isLoading } = useQuery({
    queryKey: ['liveCamera'],
    queryFn: () => liveCameraService.getAllLiveCameraInfo(),
  });

  const liveCameraList = liveCameraData?.data?.liveCamera || [];

  // Mutations
  const createLiveCameraMutation = useMutation({
    mutationFn: (data) => liveCameraService.createLiveCameraInfo(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['liveCamera']);
      setSnackbar({ open: true, message: 'Informações da câmera ao vivo criadas com sucesso!', severity: 'success' });
      handleCloseDialog();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao criar informações da câmera ao vivo', severity: 'error' });
    }
  });

  const updateLiveCameraMutation = useMutation({
    mutationFn: ({ id, data }) => liveCameraService.updateLiveCameraInfo(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['liveCamera']);
      setSnackbar({ open: true, message: 'Informações da câmera ao vivo atualizadas com sucesso!', severity: 'success' });
      handleCloseDialog();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao atualizar informações da câmera ao vivo', severity: 'error' });
    }
  });

  const deleteLiveCameraMutation = useMutation({
    mutationFn: (id) => liveCameraService.deleteLiveCameraInfo(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['liveCamera']);
      setSnackbar({ open: true, message: 'Informações da câmera ao vivo deletadas com sucesso!', severity: 'success' });
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao deletar informações da câmera ao vivo', severity: 'error' });
    }
  });

  const setActiveLiveCameraMutation = useMutation({
    mutationFn: (id) => liveCameraService.setActiveLiveCameraInfo(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['liveCamera']);
      setSnackbar({ open: true, message: 'Informações da câmera ao vivo ativadas com sucesso!', severity: 'success' });
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao ativar informações da câmera ao vivo', severity: 'error' });
    }
  });

  const handleOpenDialog = (liveCamera = null) => {
    if (liveCamera) {
      setEditingLiveCamera(liveCamera);
      setFormData({
        stream_key: liveCamera.stream_key || '',
        rtmp_url: liveCamera.rtmp_url || '',
        stream_url: liveCamera.stream_url || '',
        resolution: liveCamera.resolution || '1920x1080',
        bitrate: liveCamera.bitrate || 2500,
        fps: liveCamera.fps || 30,
        is_active: liveCamera.is_active !== undefined ? liveCamera.is_active : true
      });
    } else {
      setEditingLiveCamera(null);
      setFormData({
        stream_key: '',
        rtmp_url: '',
        stream_url: '',
        resolution: '1920x1080',
        bitrate: 2500,
        fps: 30,
        is_active: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingLiveCamera(null);
    setFormData({
      stream_key: '',
      rtmp_url: '',
      stream_url: '',
      resolution: '1920x1080',
      bitrate: 2500,
      fps: 30,
      is_active: true
    });
    setShowStreamKey(false);
  };

  const handleSubmit = () => {
    // Ensure numeric fields are properly converted
    const submitData = {
      ...formData,
      bitrate: parseInt(formData.bitrate) || 2500,
      fps: parseInt(formData.fps) || 30
    };
    
    if (editingLiveCamera) {
      updateLiveCameraMutation.mutate({ id: editingLiveCamera.id, data: submitData });
    } else {
      createLiveCameraMutation.mutate(submitData);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Tem certeza que deseja deletar estas informações da câmera ao vivo?')) {
      deleteLiveCameraMutation.mutate(id);
    }
  };

  const handleSetActive = (id) => {
    setActiveLiveCameraMutation.mutate(id);
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'success' : 'default';
  };

  const getStatusLabel = (isActive) => {
    return isActive ? 'Ativo' : 'Inativo';
  };

  const getResolutionLabel = (resolution) => {
    const resolutions = {
      '1920x1080': '1080p (Full HD)',
      '1280x720': '720p (HD)',
      '854x480': '480p (SD)',
      '640x360': '360p'
    };
    return resolutions[resolution] || resolution;
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress sx={{ color: '#FF6B35' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" sx={{ fontFamily: 'Orbitron', fontWeight: 700, color: '#FF6B35' }}>
              <Videocam sx={{ mr: 1, verticalAlign: 'middle' }} />
              Câmera ao Vivo
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                bgcolor: '#FF6B35',
                '&:hover': { bgcolor: '#FF8A65' }
              }}
            >
              Adicionar Configuração
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Instruções:</strong> Configure as informações de transmissão ao vivo para que os DJs possam configurar o OBS Studio para transmissão de vídeo.
            </Typography>
          </Alert>

          <TableContainer component={Paper} sx={{ bgcolor: 'rgba(10, 10, 10, 0.5)' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#FF6B35', fontWeight: 600 }}>RTMP URL</TableCell>
                  <TableCell sx={{ color: '#FF6B35', fontWeight: 600 }}>Stream Key</TableCell>
                  <TableCell sx={{ color: '#FF6B35', fontWeight: 600 }}>Resolução</TableCell>
                  <TableCell sx={{ color: '#FF6B35', fontWeight: 600 }}>Bitrate</TableCell>
                  <TableCell sx={{ color: '#FF6B35', fontWeight: 600 }}>FPS</TableCell>
                  <TableCell sx={{ color: '#FF6B35', fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ color: '#FF6B35', fontWeight: 600 }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {liveCameraList.map((liveCamera) => (
                  <TableRow key={liveCamera.id} hover>
                    <TableCell sx={{ color: 'white' }}>
                      <Typography variant="body2" sx={{ 
                        maxWidth: 200, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {liveCamera.rtmp_url}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      <Box display="flex" alignItems="center">
                        <Typography variant="body2" sx={{ 
                          maxWidth: 150, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          mr: 1
                        }}>
                          {showStreamKey ? liveCamera.stream_key : '••••••••••••••••'}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => setShowStreamKey(!showStreamKey)}
                          sx={{ color: '#FF6B35' }}
                        >
                          {showStreamKey ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {getResolutionLabel(liveCamera.resolution)}
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {liveCamera.bitrate} kbps
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {liveCamera.fps} fps
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(liveCamera.is_active)}
                        color={getStatusColor(liveCamera.is_active)}
                        size="small"
                        icon={liveCamera.is_active ? <CheckCircle /> : <Cancel />}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(liveCamera)}
                            sx={{ color: '#FF6B35' }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Deletar">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(liveCamera.id)}
                            sx={{ color: '#f44336' }}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                        {!liveCamera.is_active && (
                          <Tooltip title="Ativar">
                            <IconButton
                              size="small"
                              onClick={() => handleSetActive(liveCamera.id)}
                              sx={{ color: '#4caf50' }}
                            >
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {liveCameraList.length === 0 && (
            <Box textAlign="center" py={4}>
              <Videocam sx={{ fontSize: 60, color: 'rgba(255, 255, 255, 0.3)', mb: 2 }} />
              <Typography variant="h6" sx={{ opacity: 0.7 }}>
                Nenhuma configuração encontrada
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.5 }}>
                Adicione as informações de transmissão ao vivo
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Dialog para Editar/Criar Informações da Câmera ao Vivo */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingLiveCamera ? 'Editar Informações da Câmera ao Vivo' : 'Adicionar Informações da Câmera ao Vivo'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Stream Key"
              value={formData.stream_key}
              onChange={(e) => setFormData({ ...formData, stream_key: e.target.value })}
              margin="normal"
              placeholder="exemplo: live_1234567890abcdef"
              helperText="Chave de transmissão fornecida pelo servidor de streaming"
            />

            <TextField
              fullWidth
              label="RTMP URL"
              value={formData.rtmp_url}
              onChange={(e) => setFormData({ ...formData, rtmp_url: e.target.value })}
              margin="normal"
              placeholder="rtmp://exemplo.com:1935/live"
              helperText="URL do servidor RTMP para transmissão"
            />

            <TextField
              fullWidth
              label="Stream URL (Opcional)"
              value={formData.stream_url}
              onChange={(e) => setFormData({ ...formData, stream_url: e.target.value })}
              margin="normal"
              placeholder="https://exemplo.com/stream"
              helperText="URL para visualizar a transmissão (opcional)"
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Resolução</InputLabel>
              <Select
                value={formData.resolution}
                onChange={(e) => setFormData({ ...formData, resolution: e.target.value })}
                label="Resolução"
              >
                <MenuItem value="1920x1080">1080p (Full HD)</MenuItem>
                <MenuItem value="1280x720">720p (HD)</MenuItem>
                <MenuItem value="854x480">480p (SD)</MenuItem>
                <MenuItem value="640x360">360p</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Bitrate (kbps)"
              type="number"
              value={formData.bitrate}
              onChange={(e) => setFormData({ ...formData, bitrate: parseInt(e.target.value) || 2500 })}
              margin="normal"
              inputProps={{ min: 500, max: 10000 }}
              helperText="Taxa de bits em kbps (recomendado: 2500 para 1080p)"
            />

            <TextField
              fullWidth
              label="FPS"
              type="number"
              value={formData.fps}
              onChange={(e) => setFormData({ ...formData, fps: parseInt(e.target.value) || 30 })}
              margin="normal"
              inputProps={{ min: 15, max: 60 }}
              helperText="Frames por segundo (recomendado: 30)"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Configuração Ativa"
              sx={{ mt: 2 }}
            />

            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Atenção:</strong> Ao ativar uma nova configuração, a configuração anterior será desativada automaticamente.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createLiveCameraMutation.isPending || updateLiveCameraMutation.isPending}
            sx={{ bgcolor: '#FF6B35', '&:hover': { bgcolor: '#FF8A65' } }}
          >
            {(createLiveCameraMutation.isPending || updateLiveCameraMutation.isPending) ? 
              <CircularProgress size={20} /> : 'Salvar'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LiveCameraManagement;
